import React from 'react'
import { UsersIcon } from 'lucide-react'

import { Stack, Typography, useTheme } from '@mui/material'

/**
 * "4 people at your company asked for this" — the visible half of
 * `support_requests.board_item_id`.
 *
 * ## What this used to be, and why it is no longer a link
 *
 * It was a link to `/support/requests?item=<id>` — the customer feature board,
 * which VRG-324 retires. The board's problem was that it required visiting a
 * surface in order to rank things you had already asked for, so almost nobody did;
 * the +1 moved into the intake, where a colleague typing the same thing is offered
 * "add mine to this".
 *
 * With the destination gone, the honest options were to point this at
 * `Company → Support` or to stop being a link. It stops being a link. Sending the
 * reader from THIS request to a list that contains this request is a round trip to
 * where they already are — and the list is permission-gated, so for a plain member
 * the link would have gone somewhere they cannot see.
 *
 * ## What it says instead
 *
 * The COUNT, which is the thing the board was supposed to communicate and the only
 * fact here the reader does not already have on screen. "Your idea is not just
 * yours" is the answer to "did anything happen to my request?", and it is now
 * true-by-construction rather than dependent on anyone browsing a board.
 *
 * Rendered only when the count is > 1: "1 person asked for this" is a statement
 * about the request the reader is currently looking at.
 */
export const BoardItemLink = ({ requestCount }: { requestCount: number }) => {
  const theme = useTheme()

  if (requestCount <= 1) return null

  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <UsersIcon size={14} strokeWidth={1.5} color={theme.palette.text.secondary} />
      <Typography variant="body4" color="text.secondary">
        {requestCount} people at your company asked for this
      </Typography>
    </Stack>
  )
}
